/**
 * DailyNest — Unified Search Module
 * Powers both the header overlay modal and the dedicated /search/ page.
 * Reads post data from #search-data (embedded by page-search.hbs)
 * or fetches lazily on first overlay open.
 */
(function () {
    // ── Elements: Dedicated search page ──
    var pageInput = document.getElementById('search-input');
    var pageResults = document.getElementById('search-results');
    var pageEmpty = document.getElementById('search-empty');
    var pageInitial = document.getElementById('search-initial');
    var pageCount = document.getElementById('search-count');

    // ── Elements: Modal overlay (in default.hbs) ──
    var modal = document.getElementById('search-modal');
    var modalInput = document.getElementById('search-modal-input');
    var modalClose = document.getElementById('search-modal-close');
    var modalBackdrop = document.getElementById('search-modal-backdrop');

    // ── State ──
    var allPosts = [];
    var loaded = false;
    var timer = null;
    var activeIndex = -1; // keyboard nav index

    // ── Parse embedded data from page-search.hbs ──
    function parseEmbeddedData() {
        var items = document.querySelectorAll('#search-data .search-item');
        if (!items.length) return false;
        items.forEach(function (el) {
            allPosts.push({
                title: el.dataset.title || '',
                url: el.dataset.url || '',
                excerpt: el.dataset.excerpt || '',
                feature_image: el.dataset.image || '',
                reading_time: el.dataset.readingTime || '3',
                published_at: el.dataset.date || '',
                tag: el.dataset.tag ? { name: el.dataset.tag, color: el.dataset.tagColor || '' } : null,
                author: el.dataset.author ? { name: el.dataset.author } : null
            });
        });
        loaded = true;
        return true;
    }

    // ── Fetch posts via Ghost Content API (fallback for overlay) ──
    function fetchPosts(callback) {
        if (loaded) { if (callback) callback(); return; }

        // Try embedded data first
        if (parseEmbeddedData()) { if (callback) callback(); return; }

        // Fallback: fetch from the search page and parse its embedded data
        fetch('/search/')
            .then(function (res) { return res.text(); })
            .then(function (html) {
                var parser = new DOMParser();
                var doc = parser.parseFromString(html, 'text/html');
                var items = doc.querySelectorAll('#search-data .search-item');
                items.forEach(function (el) {
                    allPosts.push({
                        title: el.dataset.title || '',
                        url: el.dataset.url || '',
                        excerpt: el.dataset.excerpt || '',
                        feature_image: el.dataset.image || '',
                        reading_time: el.dataset.readingTime || '3',
                        published_at: el.dataset.date || '',
                        tag: el.dataset.tag ? { name: el.dataset.tag, color: el.dataset.tagColor || '' } : null,
                        author: el.dataset.author ? { name: el.dataset.author } : null
                    });
                });
                loaded = true;
                if (callback) callback();
            })
            .catch(function () { loaded = true; if (callback) callback(); });
    }

    // ── Search logic ──
    function search(query) {
        if (!query || query.length < 2) return [];
        var q = query.toLowerCase();
        return allPosts.filter(function (p) {
            return p.title.toLowerCase().indexOf(q) > -1 ||
                   (p.excerpt && p.excerpt.toLowerCase().indexOf(q) > -1) ||
                   (p.tag && p.tag.name && p.tag.name.toLowerCase().indexOf(q) > -1) ||
                   (p.author && p.author.name && p.author.name.toLowerCase().indexOf(q) > -1);
        });
    }

    function highlight(text, query) {
        if (!query) return text;
        var re = new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
        return text.replace(re, '<mark class="bg-[rgba(153,27,27,0.15)] text-inherit px-0.5">$1</mark>');
    }

    // ── Render results as HTML ──
    function renderResults(matched, query) {
        var html = '<div class="space-y-0">';
        matched.forEach(function (post, idx) {
            var tag = post.tag;
            var author = post.author;
            var date = post.published_at ? new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase() : '';
            var desc = post.excerpt || '';

            var imgPath = post.feature_image || '/assets/images/default-image.png';
            var img = '<div class="flex-shrink-0 relative w-[100px] h-[100px]"><img src="' + imgPath + '" alt="" class="absolute inset-0 w-full h-full object-cover' + (post.feature_image ? '' : ' opacity-90') + '" loading="lazy">'
                + (tag && tag.color ? '<div class="absolute top-1 left-1 h-1.5 w-1.5 rounded-full" style="background-color:' + tag.color + ';"></div>' : '')
                + '</div>';

            html += '<a href="' + post.url + '" class="search-result group flex gap-5 py-5 border-b border-stone-200 dark:border-stone-700/50 hover:bg-stone-50/50 dark:hover:bg-stone-800/30 transition-colors px-2 -mx-2" data-index="' + idx + '">'
                + img
                + '<div class="flex-1 min-w-0">'
                    + '<div class="flex items-center gap-2 mb-1.5 text-[12px] tracking-[0.25em] uppercase font-semibold">'
                        + (tag ? '<span style="color:' + (tag.color || 'var(--accent)') + ';">' + tag.name + '</span>' : '')
                        + '<span class="text-stone-400 dark:text-stone-600">\u00b7</span><span class="text-stone-500 dark:text-stone-400">' + date + '</span>'
                    + '</div>'
                    + '<h3 class="serif text-stone-900 dark:text-stone-100 leading-snug mb-1.5 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors text-[17px] font-bold tracking-tight">' + highlight(post.title, query) + '</h3>'
                    + (desc ? '<p class="serif text-stone-600 dark:text-stone-400 leading-relaxed line-clamp-2 text-[14px]">' + desc + '</p>' : '')
                    + '<div class="flex items-center gap-2 mt-2 text-[12px] text-stone-500 dark:text-stone-400 serif italic">'
                        + (author ? '<span>' + author.name + '</span><span>\u00b7</span>' : '')
                        + '<span>' + (post.reading_time || '3') + ' min</span>'
                    + '</div>'
                + '</div>'
            + '</a>';
        });
        html += '</div>';
        return html;
    }

    // ── Keyboard navigation for result lists ──
    function updateActiveResult(container) {
        var items = container.querySelectorAll('.search-result');
        items.forEach(function (el, i) {
            if (i === activeIndex) {
                el.classList.add('bg-[var(--paper-3)]');
            } else {
                el.classList.remove('bg-[var(--paper-3)]');
            }
        });
        if (activeIndex >= 0 && items[activeIndex]) {
            items[activeIndex].scrollIntoView({ block: 'nearest' });
        }
    }

    function handleKeyNav(e, container, resultCount) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            activeIndex = Math.min(activeIndex + 1, resultCount - 1);
            updateActiveResult(container);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            activeIndex = Math.max(activeIndex - 1, -1);
            updateActiveResult(container);
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            var items = container.querySelectorAll('.search-result');
            if (items[activeIndex]) {
                e.preventDefault();
                items[activeIndex].click();
            }
        }
    }

    // ══════════════════════════════════════════
    // DEDICATED SEARCH PAGE
    // ══════════════════════════════════════════
    if (pageInput && pageResults) {
        parseEmbeddedData();

        var currentPageResults = [];

        function runPageSearch(query) {
            activeIndex = -1;
            if (!query || query.length < 2) {
                pageResults.innerHTML = '';
                if (pageEmpty) pageEmpty.classList.add('hidden');
                if (pageInitial) pageInitial.classList.remove('hidden');
                if (pageCount) pageCount.classList.add('hidden');
                return;
            }
            if (pageInitial) pageInitial.classList.add('hidden');

            var matched = search(query);
            currentPageResults = matched;

            if (!matched.length) {
                pageResults.innerHTML = '';
                if (pageEmpty) pageEmpty.classList.remove('hidden');
                if (pageCount) pageCount.classList.add('hidden');
                return;
            }

            if (pageEmpty) pageEmpty.classList.add('hidden');
            if (pageCount) {
                pageCount.classList.remove('hidden');
                pageCount.textContent = matched.length + ' result' + (matched.length > 1 ? 's' : '');
            }
            pageResults.innerHTML = renderResults(matched, query);
        }

        pageInput.addEventListener('input', function () {
            clearTimeout(timer);
            timer = setTimeout(function () { runPageSearch(pageInput.value.trim()); }, 200);
        });

        pageInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' && activeIndex < 0) {
                clearTimeout(timer);
                runPageSearch(pageInput.value.trim());
            } else {
                handleKeyNav(e, pageResults, currentPageResults.length);
            }
        });

        // Pre-fill from URL
        var params = new URLSearchParams(window.location.search);
        var q = params.get('q');
        if (q) { pageInput.value = q; runPageSearch(q); }
    }

    // ══════════════════════════════════════════
    // MODAL OVERLAY (header search)
    // ══════════════════════════════════════════
    if (modal) {
        function openSearch() {
            modal.classList.remove('hidden');
            document.body.style.overflow = 'hidden';
            fetchPosts(function () {
                if (modalInput) {
                    setTimeout(function () { modalInput.focus(); modalInput.select(); }, 100);
                }
            });
        }

        function closeSearch() {
            modal.classList.add('hidden');
            document.body.style.overflow = '';
            activeIndex = -1;
        }

        // Search buttons throughout the site
        document.querySelectorAll('.search-btn, .search-link').forEach(function (btn) {
            btn.addEventListener('click', function (e) {
                e.preventDefault();
                openSearch();
            });
        });

        // Close handlers
        if (modalClose) modalClose.addEventListener('click', closeSearch);
        if (modalBackdrop) modalBackdrop.addEventListener('click', closeSearch);

        // Keyboard: Escape to close, Cmd/Ctrl+K to toggle
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
                closeSearch();
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                if (modal.classList.contains('hidden')) openSearch();
                else closeSearch();
            }
        });

        // Modal form submits to /search/?q=...
        var modalForm = document.getElementById('search-modal-form');
        if (modalForm) {
            modalForm.addEventListener('submit', function (e) {
                var input = document.getElementById('search-modal-input');
                if (input && !input.value.trim()) e.preventDefault();
            });
        }
    }
})();
